import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { MarketingShell } from "../components/MarketingShell";
import { getPublicSiteOrigin } from "../api/client";

export default function TermsOfServicePage() {
  const origin = getPublicSiteOrigin() || "https://setpilot.org";
  const url = `${origin}/terms-of-service`;
  const title = "Terms of service — SetPilot";
  const description =
    "Terms of use for SetPilot: acceptable use, intellectual property, disclaimers, and limitations when using the DJ set assistant.";

  return (
    <MarketingShell>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>

      <article className="m-prose">
        <h1>Terms of service</h1>
        <p className="m-lede">
          By using SetPilot (&quot;Service&quot;), you agree to these terms. If you do not agree, do not use the Service.
          Last updated: April 2026.
        </p>

        <h2>The Service</h2>
        <p>
          SetPilot provides tools to analyze audio you supply and to suggest DJ set ordering and notes. The Service is
          provided in beta and may change or be interrupted. We do not guarantee uninterrupted or error-free operation.
        </p>

        <h2>Eligibility and acceptable use</h2>
        <p>You may use the Service only in compliance with applicable law. You agree not to:</p>
        <ul>
          <li>Upload content you do not have the right to use or that infringes others&apos; rights.</li>
          <li>Attempt to probe, disrupt, or overload our systems or other users&apos; sessions.</li>
          <li>Misrepresent affiliation with SetPilot or use the Service to distribute malware or spam.</li>
        </ul>

        <h2>Your content</h2>
        <p>
          You retain rights to audio and data you submit. You grant us a limited license to host, process, transmit, and
          display that content solely to operate and improve the Service. You are responsible for the legality of your
          uploads and for obtaining licenses where required for public performance or distribution of underlying works.
        </p>

        <h2>Intellectual property</h2>
        <p>
          SetPilot branding, software, and documentation are owned by us or our licensors. Except for the limited rights
          needed to use the Service, no rights are granted to our IP.
        </p>

        <h2>Disclaimers</h2>
        <p>
          THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE.&quot; TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM
          ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
          NON-INFRINGEMENT. ANALYSIS OUTPUTS (BPM, KEY, ENERGY, ETC.) ARE ESTIMATES FOR CREATIVE PLANNING ONLY AND ARE
          NOT A SUBSTITUTE FOR PROFESSIONAL JUDGMENT OR LEGAL OR LICENSING ADVICE.
        </p>

        <h2>Limitation of liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
          CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF THE
          SERVICE. OUR AGGREGATE LIABILITY FOR ANY CLAIM RELATING TO THE SERVICE WILL NOT EXCEED THE GREATER OF (A) THE
          AMOUNT YOU PAID US FOR THE SERVICE IN THE TWELVE MONTHS BEFORE THE CLAIM OR (B) ONE HUNDRED U.S. DOLLARS (IF
          YOU HAVE NOT PAID US).
        </p>

        <h2>Indemnity</h2>
        <p>
          You will defend and indemnify us and our affiliates against claims arising from your content, your use of the
          Service, or your violation of these terms, to the extent permitted by law.
        </p>

        <h2>Changes</h2>
        <p>
          We may modify the Service or these terms. Material changes will be indicated by updating this page. Continued
          use after changes constitutes acceptance.
        </p>

        <h2>Governing law</h2>
        <p>
          These terms are governed by the laws of the United States and the State of Delaware, excluding conflict-of-law
          rules, unless your jurisdiction requires otherwise.
        </p>

        <h2>Contact</h2>
        <p>
          Questions: <Link to="/contact">Contact us</Link>.
        </p>

        <p style={{ marginTop: 28 }}>
          <Link to="/">← Back to the app</Link>
        </p>
      </article>
    </MarketingShell>
  );
}
